// components/data-quality/BookingQualityDetailsModal.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCheckCircle,
  faExclamationTriangle,
  faCalendarDay,
  faListAlt,
  faInfoCircle,
  faArrowRight,
  faAngleRight,
  faCheck,
  faTimes as faXMark,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";

const BookingQualityDetailsModal = ({ bookingData, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("status");

  if (!isOpen || !bookingData) return null;

  const {
    booking,
    statusQuality,
    dateQuality,
    overallQualityScore,
    isAbandoned,
  } = bookingData;

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "N/A";

      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return dateObj.toLocaleDateString(undefined, options);
    } catch (error) {
      return "N/A";
    }
  };

  // Get quality score color
  const getQualityColor = (score) => {
    if (score >= 90) return "text-green-600";
    if (score >= 70) return "text-blue-600";
    if (score >= 50) return "text-yellow-600";
    if (score >= 30) return "text-orange-600";
    return "text-red-600";
  };

  // Get status icon
  const getStatusIcon = (isValid) => {
    return isValid ? (
      <FontAwesomeIcon icon={faCheck} className="text-green-600" />
    ) : (
      <FontAwesomeIcon icon={faXMark} className="text-red-600" />
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      className="text-blue-600 mr-2"
                    />
                    Booking Data Quality Analysis
                  </h3>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                  </button>
                </div>

                <div className="border-b border-gray-200 mb-4">
                  <div className="flex -mb-px">
                    <button
                      className={`mr-6 py-2 px-1 font-medium text-sm border-b-2 focus:outline-none ${
                        activeTab === "status"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveTab("status")}
                    >
                      <FontAwesomeIcon icon={faListAlt} className="mr-1" />
                      Status Quality
                    </button>
                    <button
                      className={`mr-6 py-2 px-1 font-medium text-sm border-b-2 focus:outline-none ${
                        activeTab === "dates"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveTab("dates")}
                    >
                      <FontAwesomeIcon icon={faCalendarDay} className="mr-1" />
                      Date Quality
                    </button>
                    <button
                      className={`py-2 px-1 font-medium text-sm border-b-2 focus:outline-none ${
                        activeTab === "summary"
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                      onClick={() => setActiveTab("summary")}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                      Summary
                    </button>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Reference</p>
                      <p className="font-medium">{booking.referencia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Booking Code</p>
                      <p className="font-medium">{booking.bookingCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">
                        Overall Quality Score
                      </p>
                      <p
                        className={`font-medium text-lg ${getQualityColor(
                          overallQualityScore
                        )}`}
                      >
                        {overallQualityScore.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Customer</p>
                      <p className="font-medium">
                        {booking.cliente?.razonSocial || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Terminal</p>
                      <p className="font-medium">{booking.terminal || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium">
                        {isAbandoned ? (
                          <span className="text-red-600 flex items-center">
                            <FontAwesomeIcon
                              icon={faExclamationTriangle}
                              className="mr-1"
                            />
                            Abandoned
                          </span>
                        ) : (
                          <span className="text-green-600 flex items-center">
                            <FontAwesomeIcon
                              icon={faCheckCircle}
                              className="mr-1"
                            />
                            Active
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {activeTab === "status" && (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-md font-medium flex items-center mb-2">
                        <FontAwesomeIcon
                          icon={
                            statusQuality.isValid
                              ? faCheckCircle
                              : faExclamationTriangle
                          }
                          className={
                            statusQuality.isValid
                              ? "text-green-600 mr-2"
                              : "text-orange-600 mr-2"
                          }
                        />
                        Status Progression Quality:
                        <span
                          className={`ml-2 ${getQualityColor(
                            statusQuality.score
                          )}`}
                        >
                          {statusQuality.score.toFixed(1)}%
                        </span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {statusQuality.message}
                      </p>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600 mr-2">
                          Status Count:
                        </span>
                        <span className="font-medium">
                          {statusQuality.statusCount}
                        </span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-gray-600 mr-2">
                          Unique Statuses:
                        </span>
                        <span className="font-medium">
                          {statusQuality.uniqueStatusCount}
                        </span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-gray-600 mr-2">
                          Expected Statuses:
                        </span>
                        <span className="font-medium">
                          {statusQuality.expectedCount}
                        </span>
                      </div>
                    </div>

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Date
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Updated By
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Valid Progression
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Expected Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {statusQuality.details.map((detail, index) => (
                            <tr
                              key={index}
                              className={detail.isValid ? "" : "bg-red-50"}
                            >
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                {detail.status}
                                {index === 0 && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    (First)
                                  </span>
                                )}
                                {index === statusQuality.details.length - 1 && (
                                  <span className="ml-2 text-xs text-gray-500">
                                    (Latest)
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatDate(detail.date)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {booking.status[index]?.actualizadoPor || "N/A"}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                {getStatusIcon(detail.isValid)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {!detail.isValid && detail.expectedStatus ? (
                                  <div className="flex items-center">
                                    <span className="text-red-600 font-medium">
                                      {detail.expectedStatus}
                                    </span>
                                    <FontAwesomeIcon
                                      icon={faArrowRight}
                                      className="mx-2 text-gray-400"
                                    />
                                    <span>{detail.status}</span>
                                  </div>
                                ) : (
                                  <span>-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === "dates" && (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-md font-medium flex items-center mb-2">
                        <FontAwesomeIcon
                          icon={
                            dateQuality.isValid
                              ? faCheckCircle
                              : faExclamationTriangle
                          }
                          className={
                            dateQuality.isValid
                              ? "text-green-600 mr-2"
                              : "text-orange-600 mr-2"
                          }
                        />
                        Date Sequencing Quality:
                        <span
                          className={`ml-2 ${getQualityColor(
                            dateQuality.score
                          )}`}
                        >
                          {dateQuality.score.toFixed(1)}%
                        </span>
                      </h4>
                      <p className="text-sm text-gray-600 mb-2">
                        {dateQuality.isValid
                          ? "All dates are properly sequenced with no significant gaps"
                          : dateQuality.dateGapIssues.length > 0
                          ? "There are gaps greater than 7 days between some sequential dates"
                          : "Some required date fields are missing"}
                      </p>
                      <div className="flex items-center text-sm">
                        <span className="text-gray-600 mr-2">
                          Populated Dates:
                        </span>
                        <span className="font-medium">
                          {dateQuality.dateCount}
                        </span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-gray-600 mr-2">
                          Expected Dates:
                        </span>
                        <span className="font-medium">
                          {dateQuality.expectedDateCount}
                        </span>
                        <span className="mx-2 text-gray-400">|</span>
                        <span className="text-gray-600 mr-2">
                          Days Since Update:
                        </span>
                        <span
                          className={`font-medium ${
                            dateQuality.isAbandoned ? "text-red-600" : ""
                          }`}
                        >
                          {dateQuality.daysSinceLastUpdate}
                        </span>
                      </div>
                    </div>

                    {dateQuality.dateGapIssues.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-medium mb-2 flex items-center text-orange-700">
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="mr-1"
                          />
                          Date Gap Issues
                        </h5>
                        <div className="border border-orange-200 rounded-lg overflow-hidden bg-orange-50">
                          <table className="min-w-full divide-y divide-orange-200">
                            <thead className="bg-orange-100">
                              <tr>
                                <th
                                  scope="col"
                                  className="px-4 py-2 text-left text-xs font-medium text-orange-800 uppercase tracking-wider"
                                >
                                  From Field
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-2 text-left text-xs font-medium text-orange-800 uppercase tracking-wider"
                                >
                                  From Date
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-2 text-left text-xs font-medium text-orange-800 uppercase tracking-wider"
                                >
                                  To Field
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-2 text-left text-xs font-medium text-orange-800 uppercase tracking-wider"
                                >
                                  To Date
                                </th>
                                <th
                                  scope="col"
                                  className="px-4 py-2 text-left text-xs font-medium text-orange-800 uppercase tracking-wider"
                                >
                                  Gap (Days)
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-orange-200">
                              {dateQuality.dateGapIssues.map((gap, index) => (
                                <tr key={index}>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-orange-800">
                                    {gap.fromField}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-orange-800">
                                    {formatDate(gap.fromDate)}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-orange-800">
                                    {gap.toField}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm text-orange-800">
                                    {formatDate(gap.toDate)}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-orange-800">
                                    {gap.daysBetween} days
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Date Field
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Value
                            </th>
                            <th
                              scope="col"
                              className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Populated
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {[
                            { field: "fechaAlta", label: "Alta (Creation)" },
                            { field: "fechaGlosa", label: "Glosa" },
                            { field: "fechaPiso", label: "Piso" },
                            { field: "fechaAtraco", label: "Atraco" },
                            {
                              field: "fechaETA",
                              label: "Latest ETA",
                              isArray: true,
                            },
                            {
                              field: "fechaCita",
                              label: "Latest Cita",
                              isArray: true,
                            },
                            {
                              field: "fechaReprogramacion",
                              label: "Reprogramacion",
                            },
                            {
                              field: "fechaRevalidacion",
                              label: "Revalidacion",
                            },
                          ].map((dateField) => {
                            let dateValue = null;
                            let isPopulated = false;

                            if (dateField.isArray) {
                              if (
                                dateField.field === "fechaETA" &&
                                booking.fechaETA &&
                                booking.fechaETA.length > 0
                              ) {
                                const latestETA = booking.fechaETA.reduce(
                                  (latest, current) => {
                                    return !latest ||
                                      new Date(current.fechaETA) >
                                        new Date(latest.fechaETA)
                                      ? current
                                      : latest;
                                  },
                                  null
                                );
                                dateValue = latestETA
                                  ? latestETA.fechaETA
                                  : null;
                                isPopulated = !!dateValue;
                              } else if (
                                dateField.field === "fechaCita" &&
                                booking.fechaCita
                              ) {
                                // Handle fechaCita which may have complex structure
                                if (
                                  Array.isArray(booking.fechaCita) &&
                                  booking.fechaCita.length > 1 &&
                                  booking.fechaCita[1]
                                ) {
                                  dateValue =
                                    booking.fechaCita[1].fecha || null;
                                }
                                isPopulated = !!dateValue;
                              }
                            } else {
                              dateValue = booking[dateField.field];
                              isPopulated = !!dateValue;
                            }

                            return (
                              <tr
                                key={dateField.field}
                                className={!isPopulated ? "bg-red-50" : ""}
                              >
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {dateField.label}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                  {isPopulated ? formatDate(dateValue) : "-"}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                                  {getStatusIcon(isPopulated)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === "summary" && (
                  <div>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-md font-medium mb-3 flex items-center">
                          <FontAwesomeIcon
                            icon={faListAlt}
                            className="text-blue-600 mr-2"
                          />
                          Status Quality Summary
                        </h4>
                        <div className="flex items-center mb-3">
                          <div
                            className={`w-24 h-24 rounded-full flex items-center justify-center text-xl font-bold ${getQualityColor(
                              statusQuality.score
                            )} bg-gray-100`}
                          >
                            {statusQuality.score.toFixed(1)}%
                          </div>
                          <div className="ml-4">
                            <p className="text-sm mb-2">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Status Progress:{" "}
                              {statusQuality.isValid ? (
                                <span className="text-green-600">Valid</span>
                              ) : (
                                <span className="text-red-600">Invalid</span>
                              )}
                            </p>
                            <p className="text-sm mb-2">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Status Completion:{" "}
                              {statusQuality.uniqueStatusCount}/
                              {statusQuality.expectedCount} statuses
                            </p>
                            <p className="text-sm">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Latest Status:{" "}
                              {booking.status && booking.status.length > 0
                                ? booking.status[booking.status.length - 1]
                                    .status
                                : "None"}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {statusQuality.isValid ? (
                            <p className="flex items-center text-green-600">
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="mr-2"
                              />
                              Status progression follows the expected sequence
                            </p>
                          ) : (
                            <p className="flex items-center text-red-600">
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-2"
                              />
                              {statusQuality.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-md font-medium mb-3 flex items-center">
                          <FontAwesomeIcon
                            icon={faCalendarDay}
                            className="text-blue-600 mr-2"
                          />
                          Date Quality Summary
                        </h4>
                        <div className="flex items-center mb-3">
                          <div
                            className={`w-24 h-24 rounded-full flex items-center justify-center text-xl font-bold ${getQualityColor(
                              dateQuality.score
                            )} bg-gray-100`}
                          >
                            {dateQuality.score.toFixed(1)}%
                          </div>
                          <div className="ml-4">
                            <p className="text-sm mb-2">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Date Sequence:{" "}
                              {dateQuality.isValid ? (
                                <span className="text-green-600">Valid</span>
                              ) : (
                                <span className="text-red-600">Invalid</span>
                              )}
                            </p>
                            <p className="text-sm mb-2">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Date Completion: {dateQuality.dateCount}/
                              {dateQuality.expectedDateCount} dates
                            </p>
                            <p className="text-sm">
                              <FontAwesomeIcon
                                icon={faAngleRight}
                                className="text-gray-400 mr-1"
                              />
                              Date Gaps: {dateQuality.dateGapIssues.length}{" "}
                              issues detected
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {dateQuality.isAbandoned ? (
                            <p className="flex items-center text-red-600">
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-2"
                              />
                              Booking appears to be abandoned (
                              {dateQuality.daysSinceLastUpdate} days since last
                              update)
                            </p>
                          ) : dateQuality.isValid ? (
                            <p className="flex items-center text-green-600">
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="mr-2"
                              />
                              All dates are properly sequenced with no
                              significant gaps
                            </p>
                          ) : (
                            <p className="flex items-center text-orange-600">
                              <FontAwesomeIcon
                                icon={faExclamationTriangle}
                                className="mr-2"
                              />
                              {dateQuality.dateGapIssues.length > 0
                                ? `Found ${dateQuality.dateGapIssues.length} date gaps exceeding 7 days`
                                : "Some required date fields are missing"}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 border border-gray-200 rounded-lg p-4">
                      <h4 className="text-md font-medium mb-3 flex items-center">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-600 mr-2"
                        />
                        Overall Quality Assessment
                      </h4>

                      <div className="mb-4">
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                          <div
                            className={`h-4 rounded-full ${
                              overallQualityScore >= 90
                                ? "bg-green-600"
                                : overallQualityScore >= 70
                                ? "bg-blue-600"
                                : overallQualityScore >= 50
                                ? "bg-yellow-600"
                                : overallQualityScore >= 30
                                ? "bg-orange-600"
                                : "bg-red-600"
                            }`}
                            style={{ width: `${overallQualityScore}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span>Critical (0%)</span>
                          <span>Poor (30%)</span>
                          <span>Average (50%)</span>
                          <span>Good (70%)</span>
                          <span>Excellent (90%)</span>
                        </div>
                      </div>

                      <div className="text-sm">
                        <p className="mb-2 font-medium">Quality Assessment:</p>
                        <ul className="list-disc pl-5 space-y-1">
                          {overallQualityScore >= 90 ? (
                            <li className="text-green-600">
                              Excellent: This booking has complete and valid
                              data with proper progression.
                            </li>
                          ) : overallQualityScore >= 70 ? (
                            <li className="text-blue-600">
                              Good: This booking has most required data with
                              minor issues.
                            </li>
                          ) : overallQualityScore >= 50 ? (
                            <li className="text-yellow-600">
                              Average: This booking has some data quality issues
                              that should be addressed.
                            </li>
                          ) : overallQualityScore >= 30 ? (
                            <li className="text-orange-600">
                              Poor: This booking has significant data quality
                              issues requiring attention.
                            </li>
                          ) : (
                            <li className="text-red-600">
                              Critical: This booking has severe data quality
                              issues that need immediate attention.
                            </li>
                          )}

                          {statusQuality.isValid ? (
                            <li className="text-green-600">
                              Status progression is valid and follows the
                              expected sequence.
                            </li>
                          ) : (
                            <li className="text-red-600">
                              Status progression has issues:{" "}
                              {statusQuality.message}
                            </li>
                          )}

                          {statusQuality.uniqueStatusCount ===
                          statusQuality.expectedCount ? (
                            <li className="text-green-600">
                              All expected status updates are present.
                            </li>
                          ) : (
                            <li className="text-orange-600">
                              Missing{" "}
                              {statusQuality.expectedCount -
                                statusQuality.uniqueStatusCount}{" "}
                              expected status updates.
                            </li>
                          )}

                          {dateQuality.isValid ? (
                            <li className="text-green-600">
                              Date sequencing is valid with no significant gaps.
                            </li>
                          ) : dateQuality.dateGapIssues.length > 0 ? (
                            <li className="text-orange-600">
                              Found {dateQuality.dateGapIssues.length} date gaps
                              exceeding 7 days.
                            </li>
                          ) : (
                            <li className="text-orange-600">
                              Some required date fields are missing or invalid.
                            </li>
                          )}

                          {dateQuality.isAbandoned ? (
                            <li className="text-red-600">
                              Booking appears to be abandoned:{" "}
                              {dateQuality.daysSinceLastUpdate} days since last
                              update.
                            </li>
                          ) : null}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingQualityDetailsModal;
